import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Globe, ChevronDown, Check } from "lucide-react";

/**
 * Compact language picker — used in the navbar (desktop + mobile).
 * Uses a dropdown rather than 3 inline buttons to keep the navbar light at
 * smaller breakpoints.
 */
const LANGS = [
  { code: "fr", flag: "🇫🇷", label: "FR" },
  { code: "en", flag: "🇬🇧", label: "EN" },
  { code: "de", flag: "🇩🇪", label: "DE" },
];

export default function LanguageSwitcher({ variant = "navbar" }) {
  const { i18n, t } = useTranslation();
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handler = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  const current = LANGS.find((l) => l.code === (i18n.resolvedLanguage || i18n.language)) || LANGS[0];

  const change = (code) => {
    i18n.changeLanguage(code);
    setOpen(false);
  };

  const isCompact = variant === "navbar";
  return (
    <div ref={ref} className="relative" data-testid="lang-switcher">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className={`flex items-center gap-1.5 ${
          isCompact
            ? "px-2.5 py-1.5 text-[12px] tracking-[0.12em] uppercase"
            : "px-3 py-2 text-sm"
        } text-[#1A1A1A] hover:text-[#A3805B] transition-colors`}
        aria-haspopup="listbox"
        aria-expanded={open}
        data-testid="lang-switcher-toggle"
      >
        <Globe size={14} strokeWidth={1.5} />
        <span className="font-medium">{current.label}</span>
        <ChevronDown size={12} strokeWidth={1.8} className={`transition-transform ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <ul
          role="listbox"
          className="absolute right-0 mt-2 min-w-[150px] bg-white border border-[#E5E0D8] shadow-xl rounded-sm overflow-hidden z-[80]"
          data-testid="lang-switcher-menu"
        >
          {LANGS.map((l) => {
            const active = l.code === current.code;
            return (
              <li key={l.code}>
                <button
                  type="button"
                  onClick={() => change(l.code)}
                  className={`w-full flex items-center gap-2.5 px-3.5 py-2.5 text-sm hover:bg-[#FAF8F4] transition-colors ${
                    active ? "bg-[#FAF8F4] text-[#A3805B] font-medium" : "text-[#1A1A1A]"
                  }`}
                  data-testid={`lang-${l.code}`}
                >
                  <span className="text-base">{l.flag}</span>
                  <span className="flex-1 text-left">{t(`common.lang_${l.code}`)}</span>
                  {active && <Check size={14} strokeWidth={2} className="text-[#A3805B]" />}
                </button>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
}
